import { FC } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import {
    BodyTextSmall,
    ComponentTextMedium,
    Icon,
    pxToRem,
    HeaderNavigationItem,
    HeaderNavigationButton,
    Menu,
    useViewport,
} from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import {
    MenuItem as BaseMenuItem,
    MenuItems as BaseMenuItems,
    MenuPopover,
} from '@reach/menu-button';

import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { headerInnerId } from 'src/shared/layout/Header';
import { font } from 'src/shared/styles/variables';

const dropdownData = [
    {
        translationKey: 'essentials',
        items: [
            {
                icon: <Icon name="little-home" />,
                translationKey: 'resource_home',
                to: '/admin/resources',
            },
            {
                icon: <Icon name="rocket" />,
                translationKey: 'implementation_guide',
                to: '/admin/resources/admin-resources/launch-modern-health',
            },
            {
                icon: <Icon name="message" />,
                translationKey: 'faq',
                to: '/admin/resources/faqs',
            },
        ],
    },
    {
        translationKey: 'employee_topics',
        items: [
            {
                icon: <Icon name="same-direction-arrows" />,
                translationKey: 'professional_health',
                to: '/admin/resources/employee-resources/professional-health',
            },
            {
                icon: <Icon name="female-male" />,
                translationKey: 'diversity_and_inclusion',
                to: '/admin/resources/employee-resources/diversity-and-inclusion',
            },
            {
                icon: <Icon name="bolt" />,
                translationKey: 'crisis_and_stressful_events',
                to: '/admin/resources/employee-resources/crisis-and-stressful-events',
                isAlert: true,
            },
        ],
    },
    {
        translationKey: 'audience',
        items: [
            {
                icon: <Icon name="two-people" />,
                translationKey: 'admin_resources',
                to: '/admin/resources/admin-resources',
            },
            {
                icon: <Icon name="three-people" />,
                translationKey: 'employees_resources',
                to: '/admin/resources/employee-resources',
            },
            {
                icon: <Icon name="boss" />,
                translationKey: 'manager_resources',
                to: '/admin/resources/manager-resources',
            },
        ],
    },
];

export const AdminResourcesDropdown: FC = () => {
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation();
    const { enableGlobalNavigationFeature } = useFlags();
    const { VIEWPORT_LG_MIN, VIEWPORT_LG_MAX } = useViewport();

    if (enableGlobalNavigationFeature) {
        const items = dropdownData.map(section => ({
            id: section.translationKey,
            title: t(`admin:admin_resources_dropdown.${section.translationKey}.title`),
            items: section.items.map(item => ({
                id: item.translationKey,
                title: t(`admin:admin_resources_dropdown.${item.translationKey}.title`),
                description: t(`admin:admin_resources_dropdown.${item.translationKey}.description`),
                icon: item.icon.props.name,
                onAction: () => history.push(item.to),

                /**
                 * Extending item properties
                 */
                url: item.to,
            })),
        }));

        const selectedIds = items
            .map(section =>
                section.items.filter(item => item.url === location.pathname).map(item => item.id),
            )
            .flat(1);

        return (
            <HeaderNavigationItem stacked={VIEWPORT_LG_MAX}>
                <Menu
                    trigger={
                        <HeaderNavigationButton
                            icon="document"
                            text={t('admin:menu.resources.button.title')}
                            hasMenu={VIEWPORT_LG_MIN}
                        />
                    }
                    items={items}
                    columns={VIEWPORT_LG_MIN ? 3 : 1}
                    selectedIds={selectedIds}
                />
            </HeaderNavigationItem>
        );
    }

    return (
        <MenuPopover
            position={targetRect => {
                const headerOffsetLeft = document.getElementById(headerInnerId)?.offsetLeft ?? 0;

                return {
                    left: pxToRem(headerOffsetLeft + 16),
                    top: (targetRect?.top ?? 0) + (targetRect?.height ?? 0),
                    maxWidth: 'calc(100vw - 46px)', // 46px is for horizontal spacing
                };
            }}
        >
            <MenuItems>
                <Content>
                    {dropdownData.map(column => (
                        <div key={column.translationKey}>
                            <ResourceTypeText>
                                {t(`admin:admin_resources_dropdown.${column.translationKey}.title`)}
                            </ResourceTypeText>

                            {column.items.map(item => (
                                <MenuItem
                                    key={item.translationKey}
                                    onSelect={() => history.push(item.to)}
                                >
                                    <IconWrapper isAlert={item.isAlert}>{item.icon}</IconWrapper>
                                    <div>
                                        <ComponentTextMedium bold marginBottom={4}>
                                            {t(
                                                `admin:admin_resources_dropdown.${item.translationKey}.title`,
                                            )}
                                        </ComponentTextMedium>
                                        <BodyTextSmall>
                                            {t(
                                                `admin:admin_resources_dropdown.${item.translationKey}.description`,
                                            )}
                                        </BodyTextSmall>
                                    </div>
                                </MenuItem>
                            ))}
                        </div>
                    ))}
                </Content>
            </MenuItems>
        </MenuPopover>
    );
};

const Content = styled.div({
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: pxToRem(78),
    padding: pxToRem(30, 44, 40, 44),
});

const MenuItems = styled(BaseMenuItems)({
    borderRadius: pxToRem(12),
    backgroundColor: colors.white,
    boxShadow: '0px 0px 8px rgba(33, 38, 46, 0.12), 0px 4px 12px rgba(33, 38, 46, 0.16)',
});

const MenuItem = styled(BaseMenuItem)({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: pxToRem(16),
    padding: pxToRem(12, 12, 12, 8),
    marginBottom: pxToRem(8),
    minHeight: pxToRem(40),
    userSelect: 'text',
    '&:hover': {
        backgroundColor: colors.blue05,
        cursor: 'pointer',
        borderRadius: pxToRem(8),
        userSelect: 'text',
    },
});

const ResourceTypeText = styled.span({
    textTransform: 'uppercase',
    color: colors.gray55,
    display: 'block',
    marginBottom: pxToRem(8), // +12px from MenuItem padding
    ...font.medium(font.size.medium),
});

interface IconWrapperProps {
    isAlert?: boolean;
}
const IconWrapper = styled.div<IconWrapperProps>(({ isAlert = false }) => ({
    width: pxToRem(32),
    height: pxToRem(32),
    padding: pxToRem(7),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    borderRadius: '50%',
    backgroundColor: isAlert ? colors.red05 : colors.gray05,
    color: isAlert ? colors.red65 : colors.black,
}));
